import TYPES                 from "@/core/services/Changelogs/types";

import Title_ru_RU           from '@/core/misc/decorators/Title_ru_RU';
import serializeSimplified   from '@/core/misc/decorators/SerializeSimplified';
import Title_ru_RU_Class     from "@/core/misc/decorators/Title_ru_RU_Class";

import Core                  from '@/core/Core';
import Price                 from '@/core/types/Price';
import DateSimple            from '@/core/types/DateSimple';

import Partner               from '@/domains/Partners/Partner';
import Company               from '@/domains/Companies/Company';
import AbstractOrderOption   from '@/domains/Abstract/AbstractOrderOption';

import Person                from '@/domains/Persons/Person';
import Qty                   from "@/core/types/Qty";
import Sum                   from "@/core/types/Sum";
import Multiplier            from "@/core/types/Multiplier";
import VatRate               from "@/core/types/VatRate";

import type DemandV2         from '@/domains/DemandsV2/DemandV2';

/**
 * Вариант потребности — конкретное предложение поставщика внутри DemandV2.
 * Симметрично с бэком: DemandOption extends AbstractOrderOption.
 */
@Title_ru_RU_Class('Опция потребности') // demandOption → DemandOption
class DemandOption extends AbstractOrderOption {

	static Events = {
		Created: 'DemandOption:created',
		Updated: 'DemandOption:updated',
		Deleted: 'DemandOption:deleted',
	};

	/** У какой потребности вариант */
	@serializeSimplified
	public demand: DemandV2;

	/** Наименование исходное (как ввёл пользователь, без модификаций) */
	@Title_ru_RU('наименование', TYPES.STRING)
	protected _nameOrigin: string | null = null;

	/** Выбранное кол-во из этой опции для удовлетворения потребности */
	@Title_ru_RU('выбранное кол-во', TYPES.NUMBER)
	protected _qtySelected: Qty = Qty.zero();

	/** Создатель опции — обычно текущий пользователь */
	@serializeSimplified
	public responsibleBuyer: Person | null = null;

	/** ---------------- Фронтовые поля ---------------- */

	/** Сумма варианта для покупателя: priceFullBuyer * qtySelected */
	public sumBuyer: Sum | null = null;
	public sumFullBuyer: Sum | null = null;

	/** Сумма варианта для поставщика: priceSeller * qtySelected */
	public sumSeller: Sum | null = null;

	/** Сумма полная для поставщика: priceFullSeller * qtySelected */
	@Title_ru_RU('сумма', TYPES.SUM)
	public sumFullSeller: Sum | null = null;

	/** Сумма НДС поставщика */
	public vatSumSeller: Sum | null = null;

	/** Индикатор того что пользователь выбрал опцию (устаревает, используй isChosen геттер) */
	protected _isChosen: boolean | null = null;

	/** ================ Статические конструкторы ================ */

	static fromServer(demandOptionData: any, result: DemandOption = new DemandOption()): DemandOption {
		super.fromServer(demandOptionData, result)

    	result.responsibleBuyer = demandOptionData?.responsibleBuyer ? Core.utils.hydrate(demandOptionData.responsibleBuyer, Person.fromServer) : null;

    	result._qtySelected  = Core.utils.hydrate(demandOptionData.qtySelected, Qty.fromServer)!;

    	result.sumBuyer      = Core.utils.hydrate(demandOptionData.sumBuyer, Sum.fromServer);
    	result.sumFullBuyer  = Core.utils.hydrate(demandOptionData.sumFullBuyer, Sum.fromServer);
    	result.sumSeller     = Core.utils.hydrate(demandOptionData.sumSeller, Sum.fromServer);
    	result.sumFullSeller = Core.utils.hydrate(demandOptionData.sumFullSeller, Sum.fromServer);

		if (result._qtySelected.isPositive()) {
			result._isChosen = true;
		}

		return result;
	}

	/**
	 * Реквизиты потребности, перенесённые в вариант. Переносится только то,
	 * что в потребности действительно есть: ставка НДС покупателя берётся из
	 * её собственного поля, а не из категории товара — потребность заполняет
	 * его сама в сеттере productCategory, и категория у неё необязательна.
	 */
	static fromDemandV2(demandV2: DemandV2, result: DemandOption = new DemandOption()): DemandOption {
		super.fromDemandV2(demandV2, result);

		result.sumSeller     = result.priceSeller.mul(result.qtySelected);
		result.sumFullSeller = result.priceFullSeller.mul(result.qtySelected);

		result.demand = demandV2;

		result.vatRateBuyer = demandV2.vatRateBuyer;

		/** Условия НДС продавца до выбора юрлица: без них не посчитать полную
		 *  цену, а поставщика в варианте часто заводят прямо здесь, юрлица у него
		 *  ещё нет. Ставку берём локальной юрисдикции — её же поставит
		 *  setValuesBasedOnSellerCompany, когда юрлицо выберут. */
		result._vatRateSeller       = VatRate.fromServer(Core.config.localDefaultVatRate);
		result._isVatIncludedSeller = true;

		/** Множитель 1 — наценки нет. Поле не nullable, а calculatePriceMod
		 *  выходит первой строкой, если у потребности нет категории: без
		 *  умолчания в расчёт цены покупателя ушёл бы undefined. Данные
		 *  сервера здесь не затираются — объект собирается с нуля. */
		result.priceMod = Multiplier.fromPercent(0);

		return result;
	}

	/**
	 * Создание инстанса опции из другой опции с сохранением некоторых полей,
	 * но с обнулением всех остальных. Используется при копировании опции
	 * для создания сходной — поставщик и цены сохраняются в базовой логике,
	 * но id/наименование/qty/цены сбрасываются.
	 */
	static fromAnotherDemandOption(anotherDemandOption: DemandOption): DemandOption {
		const newDemandOption = Core.utils.cloneDeep(anotherDemandOption);
		newDemandOption.id = null as any;

		newDemandOption.nameOrigin = null;
		newDemandOption.name       = null;

		newDemandOption.qtyAvailable = null;

		newDemandOption.sellersCode = null;

		// reset prices
		newDemandOption._priceSeller    = Price.fromEmpty();
		newDemandOption.priceFullSeller = Price.fromEmpty();
		(newDemandOption as any).priceBuyer = Price.fromEmpty();
		newDemandOption.priceFullBuyer  = Price.fromEmpty();

		return newDemandOption;
	}

	/** ================ Геттеры/сеттеры ================ */

	/** На количество потребности: оно одно для всех опций, и их цены сравнимы. Пока потребность пустая — на 1 ед. Симметрично бэку */
	getDeliveryQty(): Qty {
		let result: Qty;

		if (Core.utils.isNullOrUndefined(this.demand?.qtyDesired) === false && this.demand.qtyDesired.isPositive() === true) {
			result = this.demand.qtyDesired;
		}
		else {
			result = new Qty(1);
		}

		return result;
	}

	/**
	 * Дата курса валюты — для опции это «сейчас».
	 * Симметрично с бэком: DemandOption::getDateForCurrencyRate() → new Timestamp('now').
	 */
	getDateForCurrencyRate(): DateSimple {
		return new DateSimple();
	}

	set seller(value: Partner | null) {
		/** При открытии карточки опции потребности, я запрашиваю
		 * полностью поставщика. То есть я обогащаю модель новым значением.
		 * Это обогащение ловит контрол VSuggestionSelect, и эмитит всплытие нового значения,
		 * но это всплытие нежелательно, ловлю я это всплытие таким незамысловатым образом.
		 * todo Нормальное решение этой проблемы кроется в том чтобы запрашивать опцию со всеми вложенными
		 * объектами отдельным запросом перед открытием карточки */
		if (this.seller && value?.id && value.id === this.seller?.id) return;

		/** чтобы не мутировать исходный аргумент и не ловить сайд эффекты в других местах */
		let _value: any = Core.utils.cloneDeep(value);
		/** Если пользователь создаёт нового селлера */
		if (value && value?.name && !value?.id) {
			_value = new Partner();
			_value.name = value.name;
			/** исходим из утверждения что новый поставщик местный */
			_value.country = Core.currentPartner.country;
		}

		super.seller = _value;
	}

	get seller() {
		return super.seller;
	}

	set sellerCompany(value: Company | null) {
		/** Суть в том что мы программно создаём sellerCompany, а значит когда внутри VSuggestionSelect
		 * срабатывает вотчер на новое значение, то нам не нужно переприсваивать значение в модель,
		 * оно там и так актуальное, а если принять значение из контрола, то оно испортит нам состояние */
		if (this.sellerCompany && value?.name === this.sellerCompany?.name) return;
		super.sellerCompany = value;
	}

	get sellerCompany() {
		return super.sellerCompany;
	}

	set nameOrigin(value: string | null) {
		this._nameOrigin = value;

		this.calculateName();
	}

	get nameOrigin(): string | null {
		return this._nameOrigin;
	}

	set sellersCode(value: string | null) {
		super.sellersCode = value;
		this.calculateName();
	}

	get sellersCode(): string | null {
		return super.sellersCode;
	}

	set priceSeller(value: Price) {
		super.priceSeller = value;

		this.calculateSums();
	}

	get priceSeller(): Price {
		return super.priceSeller;
	}

	get isChosen(): boolean {
		return this.qtySelected.isPositive();
	}

	/** Значение выбран ли вариант де-факто */
	set qtySelected(value: Qty) {
		this._qtySelected = value;

		/** Распределение по заявкам пересчитывать не нужно: DemandV2.distribution —
		 *  геттер, он считает от текущего qtySelected при следующем чтении. */
		this.calculateSums();
	}

	get qtySelected(): Qty {
		return this._qtySelected;
	}

	set offerCurrencyCode(value: string) {
		super.offerCurrencyCode = value;

		this.calculateSums();
	}

	get offerCurrencyCode(): string {
		return super.offerCurrencyCode;
	}

	/** ================ Действия пользователя ================ */

	choose(): void {
		if (!this.qtySelected.isPositive()) {
			this.selectAsSatisfyingDesiredQty();
		} else {
			this.resetQtySelected();
		}
	}

	resetQtySelected(): void {
		this.qtySelected = Qty.zero();
	}

	selectAsSatisfyingDesiredQty(): void {
		this.qtySelected = this.qtyAvailable!;
		this.updateAvailableQty();
	}

	updateAvailableQty(): void {
		/** Если пользователь ранее не вводил доступное кол-во,
		 * но ввёл выбранное, то соответственно нужно синкануть кол-ва */
		/** Проверка на null идёт первой: lessThan — метод, на null его не вызвать.
		 *  Прежний порядок держался на том, что null в сравнении приводился к 0. */
		if (this.qtyAvailable === null || this.qtyAvailable.lessThan(this.qtySelected)) {
			this.qtyAvailable = this.qtySelected;
		}
	}

	hasQtySelected(): boolean {
		return this.qtySelected.isPositive();
	}

	/** ================ Расчёты ================ */

	setValuesBasedOnSellerCompany(): void {
		if (this._sellerCompany?.id) {
			super.setValuesBasedOnSellerCompany();
		}

		/** Юрлица ещё нет — условия НДС не трогаем: их проставил fromDemandV2
		 *  по локальной юрисдикции, и без них не посчитать полную цену.
		 *  Обнулять можно только зная, что юрлицо НДС не платит. */
		if (Core.utils.isNullOrUndefined(this._sellerCompany)) {
			return;
		}

		if (this._sellerCompany.isVatPayer) {
			this._isVatIncludedSeller = true;

			/** Ставка по убыванию точности: собственная ставка юрлица → ставка его
			 *  системы налогообложения → локальная юрисдикция.
			 *
			 *  Откат до юрисдикции — не исключение, а рабочий путь: узбекская
			 *  сторона заводит контрагентов неявно, по ходу работы, и реквизиты
			 *  юрлица остаются незаполненными (справочник систем налогообложения
			 *  к тому же собран под РФ). Ставка здесь — умолчание до того, как её
			 *  проставят в карточке явно. Пустая ставка у плательщика НДС роняет
			 *  calculateFullPriceSeller: расчёт туда зайдёт по isVatPayer
			 *  выбранного юрлица, а vatRateSeller уйдёт в него как null. */
			if (this._sellerCompany.vatRate) {
				this._vatRateSeller = this._sellerCompany.vatRate;
			}
			else if (this._sellerCompany.taxSystem?.vatRate) {
				this._vatRateSeller = this._sellerCompany.taxSystem.vatRate;
			}
			else {
				this._vatRateSeller = VatRate.fromServer(Core.config.localDefaultVatRate);
			}
		} else {
			this._isVatIncludedSeller = null;
			this._vatRateSeller       = null;
		}
	}

	calculateName(): void {
		let result = '';

		if (this.sellersCode) {
			result += `${this.sellersCode} `;
		}

		if (this.nameOrigin) {
			result += this.nameOrigin;
		}

		this.name = result;
	}

	calculatePriceMod(): void {
		if (!this.demand?.productCategory) return;

		let additionalPayment = this.demand.productCategory.priceModProfit;

		if (this._isDeliveryNeeded) {
			/** Множители складываются по отклонениям от базы: 1.9% + 6% = 7.9%,
			 *  а не последовательным применением. См. Multiplier.plus. */
			additionalPayment = additionalPayment.plus(this.demand.productCategory.priceModTransport);
		}

		this.priceMod = additionalPayment;
	}

	async calculatePriceFullBuyerWithDeferredPayment(): Promise<void> {
		// Оставим до лучших времён
	}

	calculateSums(): void {
		if (!this.canCalculateSums()) return;

		this.calculateBuyersSums();
		this.calculateSellersSums();
		this.calculateVatSumSeller();
	}

	calculateBuyersSums(): void {
		// this.sumFullBuyer = this.priceFullBuyer.mul(this.qtySelected);
	}

	calculateSellersSums(): void {
		this.sumSeller     = this.priceSeller.mul(this.qtySelected);
		this.sumFullSeller = this.priceFullSeller.mul(this.qtySelected);
	}

	canCalculateSums(): boolean {
		/** qtySelected — объект и истинен всегда, поэтому проверяем значение:
		 *  прежнее Boolean(qtySelected) отсеивало ноль, isPositive делает то же. */
		return this.qtySelected.isPositive() && Boolean(this.priceSeller) && Boolean(this.offerCurrencyCode);
	}

	calculateVatSumSeller(): void {
		this.vatSumSeller = this.sumSeller!.getVat(this.isVatIncludedSeller!, this.vatRateSeller);
	}
}

export default DemandOption;